import { useMemo } from "react";
import useRoles from "admin/hooks/useRoles";
import { UserRole } from "admin/types/interfaces/user";
import { canAssignRole, sortFunctionalRoles } from "admin/utils/roles";
import { Checkbox } from "~/components/ui/checkbox";
import { Label } from "~/components/ui/label";

type UserRoleWithCheckedState = UserRole & {
  checked?: "indeterminate" | boolean;
};
const FunctionalRoleSelector = ({
  selectedRoles,
  setSelectedRoles,
  disabled = false,
}: {
  selectedRoles: UserRoleWithCheckedState[];
  setSelectedRoles: (roles: UserRoleWithCheckedState[]) => void;
  disabled?: boolean;
}) => {
  const { data: roles = [] } = useRoles();
  const sortedRoles = useMemo(() => [...roles].sort(sortFunctionalRoles), [roles]);

  if (sortedRoles.length === 0) {
    return <p className="text-sm text-muted-foreground py-3">No roles available.</p>;
  }

  return (
    <div className="flex flex-col divide-y divide-border">
      {sortedRoles.map((role) => {
        const isChecked = selectedRoles.some((r) => r.id === role.id);
        const isAssignable = canAssignRole(role);
        return (
          <div key={role.id} className="flex gap-3 py-3 first:border-t last:pb-0">
            <Checkbox
              id={role.id}
              disabled={disabled || !isAssignable}
              checked={isChecked}
              onCheckedChange={(checked) => {
                if (checked) {
                  setSelectedRoles([...selectedRoles, { id: role.id, name: role.name }]);
                } else {
                  setSelectedRoles(selectedRoles.filter((r) => r.id !== role.id));
                }
              }}
            />
            <div className="flex flex-col gap-1">
              <Label className="leading-[18px]" htmlFor={role.id}>
                {role.name}
              </Label>
              <p className="text-xs text-muted-foreground">{role.description}</p>
              {!isAssignable && (
                <p className="text-xs text-muted-foreground">
                  {isChecked
                    ? "You do not have permission to un-assign this role."
                    : "You do not have permission to assign this role."}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FunctionalRoleSelector;
